/* DirectiePage — Omzetkansen-machine + GGD-inspectiemodus + Aanbod-preview links.
 * Alleen toegankelijk voor directie/management-rollen.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { getImpersonatedOrg, setImpersonatedOrg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import DateField from "@/components/DateField";
import { toast } from "sonner";
import {
  Loader2, TrendingUp, TrendingDown, Download, FileText,
  ArrowRight, Calendar, MailCheck, CheckCircle2, XCircle,
  AlertTriangle, Users, Landmark, RefreshCw, Database, Building2, Clock,
  Sparkles, Mic, BookOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const eur = (n) => new Intl.NumberFormat("nl-NL", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0,
}).format(n || 0);

const fitLabel = (s) => {
  if (s >= 0.85) return { txt: "Perfect", cls: "bg-emerald-100 text-emerald-800" };
  if (s >= 0.65) return { txt: "Goed",     cls: "bg-teal-100 text-teal-800" };
  if (s >= 0.40) return { txt: "Redelijk", cls: "bg-amber-100 text-amber-800" };
  return { txt: "Zwak", cls: "bg-slate-100 text-slate-700" };
};

function PlatformAdminBar() {
  // Alleen zichtbaar voor org-onafhankelijke superadmins (platform_admin OF
  // superadmin ZONDER gekoppelde organization_id — d.w.z. de "platform"-superadmin).
  // NIET zichtbaar tijdens actieve supportmodus — dan is de user bezig een
  // specifieke KDV te ondersteunen en horen platform-brede functies verborgen.
  const { user } = useAuth();
  const rawPlatform =
    user?.role === "platform_admin" ||
    (user?.role === "superadmin" && !user?.organization_id);
  const isPlatform = rawPlatform && !getImpersonatedOrg();
  const [info, setInfo] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [impersonating, setImpersonating] = useState(() => getImpersonatedOrg());

  const loadInfo = async () => {
    try {
      const [i, o] = await Promise.all([
        api.get("/admin/demo/info"),
        api.get("/organizations").catch(() => ({ data: [] })),
      ]);
      setInfo(i.data);
      setOrgs(Array.isArray(o.data) ? o.data : (o.data?.items || []));
    } catch { /* silent */ }
  };
  useEffect(() => { if (isPlatform) loadInfo(); }, [isPlatform]);

  const switchTo = (orgId) => {
    setImpersonatedOrg(orgId);
    setImpersonating(orgId);
    toast.success(orgId
      ? `Nu kijkend als ${(orgs.find(o => o.id === orgId)?.name) || orgId}`
      : "Impersonatie gestopt — terug naar cross-tenant view");
    // Hard-reload zodat alle actieve queries (Actiecentrum, Omzetkansen…)
    // opnieuw gefetched worden met de nieuwe header.
    setTimeout(() => window.location.reload(), 500);
  };

  if (!isPlatform) return null;

  const herstel = async () => {
    if (!window.confirm(
      "Alle Regenboog-demo data (36 kinderen, 14 medewerkers, alle contracten en facturen) wordt gewist en opnieuw geplaatst. Doorgaan?"
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/demo/seed-regenboog");
      toast.success(`Regenboog-demo hersteld — ${r.data.counts.children} kinderen, ${r.data.counts.medewerkers} medewerkers`);
      await loadInfo();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Herstel mislukt");
    } finally { setBusy(false); }
  };

  const c = info?.counts || {};
  const demoIntact = info?.seeded && (c.children >= 36) && (c.medewerkers >= 14);

  return (
    <Card className="p-4 border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-slate-50"
          data-testid="platform-admin-bar">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Database className="h-5 w-5 text-indigo-700 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-slate-900">
              Platform-superadmin (organisatie-onafhankelijk)
            </div>
            <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap gap-x-4 gap-y-1">
              <span>
                <strong>{orgs.length}</strong> organisatie{orgs.length !== 1 ? "s" : ""}
              </span>
              <span data-testid="platform-admin-demo-status">
                Regenboog-demo:{" "}
                {demoIntact ? (
                  <span className="text-emerald-700 font-medium">
                    ✓ intact ({c.children} kids · {c.medewerkers} pm)
                  </span>
                ) : (
                  <span className="text-amber-700 font-medium">
                    ⚠ ontbreekt of onvolledig
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={loadInfo}
                  data-testid="platform-admin-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Ververs
          </Button>
          <Button size="sm" onClick={herstel} disabled={busy}
                  className="bg-indigo-700 hover:bg-indigo-800"
                  data-testid="platform-admin-herstel-demo">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> :
              <Database className="h-3.5 w-3.5 mr-1" />}
            Regenboog-demo herstellen
          </Button>
        </div>
      </div>
      {orgs.length > 0 && (
        <div className="mt-3 border-t border-indigo-100 pt-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              Tenant-switcher — bekijk als een specifieke organisatie
            </div>
            {impersonating && (
              <button
                onClick={() => switchTo(null)}
                className="text-[11px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold hover:bg-amber-200"
                data-testid="tenant-switcher-clear"
              >
                ✕ Stop impersonatie
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="tenant-switcher-list">
            {orgs.slice(0, 20).map(o => {
              const active = impersonating === o.id;
              return (
                <button
                  key={o.id}
                  onClick={() => switchTo(active ? null : o.id)}
                  className={`text-xs px-2 py-1 rounded flex items-center gap-1 transition ${
                    active
                      ? "bg-indigo-700 text-white border border-indigo-700 shadow-sm"
                      : "bg-white border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50"
                  }`}
                  data-testid={`tenant-switcher-${o.id}`}
                >
                  <Building2 className={`h-3 w-3 ${active ? "" : "text-slate-400"}`} />
                  {o.name || o.naam}
                  {o.id === "demo_regenboog" && (
                    <span className={`ml-1 text-[10px] px-1 rounded ${
                      active ? "bg-white/20" : "bg-amber-100 text-amber-800"
                    }`}>DEMO</span>
                  )}
                </button>
              );
            })}
            {orgs.length > 20 && (
              <span className="text-xs text-slate-500">+{orgs.length - 20} meer</span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function OmzetkansenCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/directie/omzetkansen");
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Kon omzetkansen niet laden";
      setError(msg);
      // Wis oude data zodat we geen misleidende "0 kansen" of "€0" tonen
      // bij een 403/500 na een eerdere succesvolle load.
      setData(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reserveer = async (wid) => {
    setBusyId(wid);
    try {
      const r = await api.post(`/directie/omzetkansen/${wid}/reserveer`);
      toast.success(`Plek gereserveerd in ${r.data.group_name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reservering mislukt");
    } finally { setBusyId(null); }
  };

  if (loading) return (
    <Card className="p-6" data-testid="omzetkansen-loading">
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Omzetkansen berekenen …
      </div>
    </Card>
  );

  if (error) return (
    <Card className="p-6 border-2 border-red-200 bg-red-50" data-testid="omzetkansen-error">
      <div className="flex items-center justify-between gap-4">
        <div className="text-red-800 text-sm">
          <div className="font-semibold">Omzetkansen niet beschikbaar</div>
          <div className="text-red-700 mt-0.5">{error}</div>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="omzetkansen-retry">
          Opnieuw proberen
        </Button>
      </div>
    </Card>
  );

  const kansen = data?.kansen || [];

  return (
    <Card className="overflow-hidden" data-testid="omzetkansen-card">
      <div className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 border-b border-emerald-200/60">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-700" />
              Omzetkansen-machine
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Wachtlijst gekoppeld aan vrije plekken — direct te reserveren en aan te bieden.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={load} data-testid="omzetkansen-refresh">
            Vernieuwen
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
          <Stat label="Kansen" value={data?.totaal_kansen ?? 0}
                sub={`${data?.totaal_matched ?? 0} matchbaar`} testid="stat-kansen"/>
          <Stat label="Potentie/mnd" value={eur(data?.totaal_potentie_maand)}
                accent="text-emerald-700" testid="stat-potentie-mnd"/>
          <Stat label="Potentie/jaar" value={eur(data?.totaal_potentie_jaar)}
                accent="text-emerald-700" testid="stat-potentie-jaar"/>
          <Stat label="Zonder plek" value={eur(data?.gemiste_omzet_pm)}
                icon={<TrendingDown className="h-4 w-4 text-red-600" />}
                accent="text-red-700" sub="omzet die je nu misloopt" testid="stat-gemist"/>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {kansen.length === 0 && (
          <div className="p-6 text-sm text-slate-500 italic">
            Geen actieve wachtlijst-inschrijvingen op dit moment.
          </div>
        )}
        {kansen.map(k => (
          <KansRow key={k.wachtlijst_id} k={k} busy={busyId === k.wachtlijst_id}
                   onReserveer={() => reserveer(k.wachtlijst_id)}
                   onOpenWachtlijst={() => nav("/app/wachtlijst")} />
        ))}
      </div>
    </Card>
  );
}

function Stat({ label, value, sub, accent = "text-slate-900", icon, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 flex items-center gap-2 ${accent}`}>
        {icon}{value}
      </div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function KansRow({ k, busy, onReserveer, onOpenWachtlijst }) {
  const mg = k.matched_group;
  const fit = fitLabel(k.fit_score);
  const isReserved = k.status === "aangeboden";
  return (
    <div className={`p-4 transition ${isReserved ? "bg-emerald-50/40" : "hover:bg-slate-50/60"}`}
         data-testid={`omzetkans-row-${k.wachtlijst_id}`}
         data-status={k.status}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-900 truncate flex items-center gap-2">
            {k.kind_naam}
            {isReserved && (
              <span className="text-[10px] px-1.5 py-0.5 bg-emerald-600 text-white rounded font-semibold uppercase"
                    data-testid={`omzetkans-badge-aangeboden-${k.wachtlijst_id}`}>
                gereserveerd
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
            <span>{k.leeftijd_maanden != null ? `${k.leeftijd_maanden} mnd` : "geen geb.datum"}</span>
            {k.gewenste_startdatum && <span>Start: {k.gewenste_startdatum}</span>}
            <span>{k.uren_per_week}u/w · €{k.uurtarief}/u</span>
            {isReserved && k.aangeboden_op && (
              <span className="text-emerald-700">
                Aangeboden op {String(k.aangeboden_op).slice(0, 10)}
              </span>
            )}
          </div>
        </div>
        <div className="text-right min-w-[110px]">
          <div className="text-sm font-semibold text-emerald-700">{eur(k.verwachte_maandomzet)}</div>
          <div className="text-[11px] text-slate-500">per maand</div>
        </div>
        <div className="min-w-[180px]">
          {mg ? (
            <>
              <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                <ArrowRight className="h-3.5 w-3.5 text-emerald-600" />
                {mg.group_name}
              </div>
              <div className="text-[11px] text-slate-500">
                {mg.location_name} · {mg.vrije_plekken} plek{mg.vrije_plekken !== 1 ? "ken" : ""} vrij
              </div>
            </>
          ) : (
            <div className="text-xs text-red-600 italic">Geen plek beschikbaar</div>
          )}
        </div>
        {!isReserved && <span className={`text-[11px] px-2 py-0.5 rounded ${fit.cls}`}>{fit.txt}</span>}
        {isReserved ? (
          <Button size="sm" variant="outline" onClick={onOpenWachtlijst}
                  data-testid={`omzetkans-view-${k.wachtlijst_id}`}>
            Bekijk
          </Button>
        ) : mg ? (
          <Button size="sm" onClick={onReserveer} disabled={busy}
                  className="bg-emerald-700 hover:bg-emerald-800"
                  data-testid={`omzetkans-reserveer-${k.wachtlijst_id}`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Reserveer"}
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={onOpenWachtlijst}
                  data-testid={`omzetkans-open-${k.wachtlijst_id}`}>
            Open wachtlijst
          </Button>
        )}
      </div>
    </div>
  );
}

function GgdExportCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(today);

  const loadPreview = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/directie/ggd/inspectie/preview");
      setPreview(r.data);
    } catch (e) {
      // 403 → verberg (geen director); alle andere fouten → tonen met retry.
      if (e?.response?.status === 403) { setPreview(null); }
      else { setError(e?.response?.data?.detail || "GGD-preview kon niet worden geladen"); }
    } finally { setLoading(false); }
  };
  useEffect(() => { loadPreview(); }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const url = `${api.defaults.baseURL}/directie/ggd/inspectie.zip?date_from=${dateFrom}&date_to=${dateTo}`;
      const r = await api.get(`/directie/ggd/inspectie.zip?date_from=${dateFrom}&date_to=${dateTo}`,
                              { responseType: "blob" });
      const blobUrl = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `ggd-inspectiepakket_${dateFrom}_${dateTo}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("GGD-inspectiepakket gedownload");
    } catch (e) {
      toast.error("Download mislukt: " + (e?.response?.data?.detail || e.message));
    } finally { setDownloading(false); }
  };

  if (loading) return null;
  if (error) return (
    <Card className="p-6 border-2 border-red-200 bg-red-50" data-testid="ggd-export-error">
      <div className="flex items-center justify-between gap-4">
        <div className="text-red-800 text-sm">
          <div className="font-semibold">GGD-inspectiemodus niet beschikbaar</div>
          <div className="text-red-700 mt-0.5">{error}</div>
        </div>
        <Button size="sm" variant="outline" onClick={loadPreview} data-testid="ggd-export-retry">
          Opnieuw proberen
        </Button>
      </div>
    </Card>
  );
  if (!preview) return null;
  const c = preview.counts || {};

  return (
    <Card className="p-6" data-testid="ggd-export-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-700" />
            GGD-inspectiemodus
          </h2>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Download in één klik het complete inspectie-pakket (BKR, roosters, diploma's,
            presentie <strong>én je eigen geüploade documenten</strong>) als ZIP-bestand.
            Alle CSV's zijn Excel-vriendelijk (UTF-8 BOM).
          </p>
          <a href="/app/inspectiepakket"
             className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:underline mt-1"
             data-testid="cockpit-open-inspectiepakket">
            <FileText className="h-3.5 w-3.5" /> Beheer eigen documenten &amp; bekijk downloadgeschiedenis →
          </a>
        </div>
      </div>
      {preview.eigen_documenten_count > 0 && (
        <div className="mb-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2 flex items-center gap-2"
             data-testid="cockpit-own-docs-hint">
          <FileText className="h-3.5 w-3.5" />
          <span><strong>{preview.eigen_documenten_count}</strong> eigen document(en) worden meegenomen in de ZIP (map <code>eigen-documenten/</code>).</span>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-5">
        <Fact label="Groepen" v={c.groepen} />
        <Fact label="Actieve kinderen" v={c.kinderen_actief} />
        <Fact label="Medewerkers" v={c.medewerkers_actief} />
        <Fact label="Actieve contracten" v={c.contracten_actief} />
        <Fact label="Rooster-shifts (30d)" v={c.rooster_shifts} />
        <Fact label="Presentie-regels (30d)" v={c.presentie_regels} />
        <Fact label="BKR-afwijkingen" v={c.bkr_afwijkingen} warn={c.bkr_afwijkingen > 0} />
        <Fact label="Incidenten" v={c.incidenten} warn={c.incidenten > 0} />
      </div>
      {preview.inhoud && preview.inhoud.length > 0 && (
        <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="ggd-inhoud-checklist">
          <div className="text-xs font-semibold text-emerald-900 uppercase tracking-widest mb-2">Pakket-inhoud ({preview.inhoud.length} bestanden)</div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-emerald-800">
            {preview.inhoud.map((item, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-emerald-600 shrink-0">✓</span>
                <span className={item.startsWith("rapport.pdf") ? "font-semibold" : ""}>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-48">
          <label className="text-xs text-slate-500 block mb-1">Van</label>
          <DateField value={dateFrom} onChange={setDateFrom} max={dateTo}
                     data-testid="ggd-date-from" />
        </div>
        <div className="w-48">
          <label className="text-xs text-slate-500 block mb-1">Tot</label>
          <DateField value={dateTo} onChange={setDateTo} min={dateFrom} max={today}
                     data-testid="ggd-date-to" />
        </div>
        <Button onClick={download} disabled={downloading}
                className="bg-indigo-700 hover:bg-indigo-800"
                data-testid="ggd-download-btn">
          {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
          Download inspectiepakket (.zip)
        </Button>
      </div>
    </Card>
  );
}

function GgdHistorieCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/directie/ggd/inspectie/historie?limit=10")
      .then(r => setItems(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const reDownload = (h) => {
    const url = `/api/directie/ggd/inspectie.zip?date_from=${h.date_from}&date_to=${h.date_to}`;
    window.open(url, "_blank");
  };

  return (
    <Card className="p-6" data-testid="ggd-historie-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Database className="h-5 w-5 text-slate-600" /> GGD-inspectie historie
          </h2>
          <p className="text-sm text-slate-600 mt-1">Eerder gedownloade pakketten — wie, wanneer, welke periode. Klik "Opnieuw" om dezelfde periode nogmaals te genereren.</p>
        </div>
      </div>
      {loading && <div className="text-sm text-slate-500 py-3 text-center">Laden…</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm text-slate-500 py-3 text-center">Nog geen downloads geregistreerd — de eerste GGD-download komt hier terecht.</div>
      )}
      {!loading && items.length > 0 && (
        <div className="space-y-1">
          {items.map(h => (
            <div key={h.id} className="flex items-center gap-3 py-2 px-2 rounded hover:bg-slate-50 border-b border-slate-100 last:border-0" data-testid={`ggd-historie-row-${h.id}`}>
              <div className="text-xs font-mono text-slate-500 w-32 shrink-0">{(h.downloaded_at || "").slice(0, 16).replace("T", " ")}</div>
              <div className="text-sm text-slate-800 flex-1 min-w-0">
                <div className="truncate">{h.downloaded_by_name || h.downloaded_by_email || "—"}</div>
                <div className="text-[11px] text-slate-500 font-mono">
                  {h.date_from} → {h.date_to} · {Math.round((h.bytes || 0) / 1024)} KB · {h.n_files} bestanden
                </div>
              </div>
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => reDownload(h)} data-testid={`ggd-historie-redownload-${h.id}`}>
                <Download className="h-3.5 w-3.5 mr-1" /> Opnieuw
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


function DrieUursRegelCard() {
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/bkr/afwijk-log/vandaag?datum=${datum}`);
      setData(r.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [datum]);

  return (
    <Card className="p-6" data-testid="drie-uurs-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-600" /> Drie-uursregeling — dagoverzicht
          </h2>
          <p className="text-sm text-slate-600 mt-1">Per groep: hoeveel BKR-afwijkingsminuten er vandaag gebruikt zijn en hoeveel er nog rest van de wettelijke 180 min.</p>
        </div>
        <Input type="date" value={datum} onChange={e => setDatum(e.target.value)} className="max-w-[160px] h-9" data-testid="drie-uurs-datum" />
      </div>
      {loading && <div className="text-sm text-slate-500 py-6 text-center">Laden…</div>}
      {!loading && data && data.groepen.length === 0 && (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-3 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" /> Geen BKR-afwijkingen geregistreerd op {data.datum}
        </div>
      )}
      {!loading && data && data.groepen.map(g => (
        <div key={g.group_id} className="mb-3 rounded-lg border border-slate-200 bg-white p-3" data-testid={`drie-uurs-groep-${g.group_id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-slate-900">{g.group_name}</div>
            <div className="flex items-center gap-2 text-xs">
              <span className={`px-2 py-1 rounded ${g.overschrijding ? "bg-red-100 text-red-800" : g.totaal_gebruikt_min > 120 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                Gebruikt: {g.totaal_gebruikt_min} min
              </span>
              <span className={`px-2 py-1 rounded ${g.resterend_min < 30 ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-700"}`}>
                Resterend: {g.resterend_min} min
              </span>
              {g.overschrijding && <span className="px-2 py-1 rounded bg-red-600 text-white font-semibold text-[10px] uppercase">Overschrijding</span>}
            </div>
          </div>
          <div className="flex items-end gap-0.5 h-16" data-testid={`drie-uurs-histogram-${g.group_id}`}>
            {g.uur_histogram.map(h => (
              <div key={h.uur} className="flex-1 flex flex-col items-center justify-end" title={`${h.uur} - ${h.minuten} min`}>
                <div
                  className={`w-full rounded-t ${h.minuten >= 55 ? "bg-red-500" : h.minuten >= 30 ? "bg-amber-400" : h.minuten > 0 ? "bg-emerald-400" : "bg-slate-100"}`}
                  style={{ height: `${Math.max(2, Math.min(60, h.minuten))}px` }}
                />
                <div className="text-[9px] text-slate-400 mt-1">{h.uur.slice(0, 2)}</div>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-slate-400 flex justify-between mt-1">
            <span>07u</span><span>13u</span><span>19u</span>
          </div>
          <div className="mt-2 space-y-1">
            {g.periodes.map((p, i) => (
              <div key={i} className="text-xs text-slate-600 flex items-center gap-2">
                <span className="font-mono">{p.start}</span>-<span className="font-mono">{p.eind}</span>
                <span className="text-slate-400">·</span>
                <span>{p.minuten} min · {p.aanwezig_pm}/{p.vereist_pm} PM</span>
                {p.reden && <span className="text-slate-500 italic">- {p.reden}</span>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </Card>
  );
}

function AiUsageCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/schriftje/ai-usage?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const perKind = data?.per_kind || {};
  const t = perKind.transcribe || { count: 0, cost_usd: 0, audio_bytes: 0, success: 0 };
  const a = perKind.ai_assist || { count: 0, cost_usd: 0, success: 0 };
  const s = perKind.split_per_kind || { count: 0, cost_usd: 0, n_splits: 0, success: 0 };
  const totalUsd = data?.total_cost_usd || 0;
  const totalEur = totalUsd * 0.92; // ~ USD → EUR
  const audioMin = Math.round((t.audio_bytes || 0) / 50000 / 60 * 10) / 10;

  return (
    <Card className="p-6 bg-gradient-to-br from-purple-50 to-fuchsia-50 border-purple-200/60"
          data-testid="ai-usage-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-700" />
            AI-Schriftje kosten
          </h3>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Werkelijk verbruik van Whisper (spraak-naar-tekst) + Claude (schriftje-generatie
            en splitser). Cijfers per organisatie, exclusief BTW.
          </p>
        </div>
        <div className="flex gap-1" data-testid="ai-usage-period-picker">
          {[7, 30, 90].map(d => (
            <Button key={d} size="sm"
                    variant={days === d ? "default" : "outline"}
                    onClick={() => setDays(d)}
                    className="h-7 px-3 text-[11px]"
                    style={days === d ? { background: "var(--sage)" } : {}}
                    data-testid={`ai-usage-days-${d}`}>
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-6 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Verbruik laden…
        </div>
      )}
      {!loading && data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-lg bg-white border border-purple-200 p-3"
               data-testid="ai-usage-total">
            <div className="text-[10px] uppercase tracking-widest text-purple-700 font-semibold">
              Totaal
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1">
              €{totalEur.toFixed(2)}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              ${totalUsd.toFixed(4)} · {data.total_events} events
            </div>
          </div>
          <div className="rounded-lg bg-white border border-slate-200 p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
              <Mic className="h-3 w-3" /> Spraak
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1"
                 data-testid="ai-usage-transcribe-count">
              {t.count}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              ~{audioMin}min · ${(t.cost_usd || 0).toFixed(4)}
            </div>
          </div>
          <div className="rounded-lg bg-white border border-slate-200 p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
              <BookOpen className="h-3 w-3" /> Schriftjes
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1"
                 data-testid="ai-usage-assist-count">
              {a.count}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              ${(a.cost_usd || 0).toFixed(4)}
            </div>
          </div>
          <div className="rounded-lg bg-white border border-slate-200 p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> Split-per-kind
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1"
                 data-testid="ai-usage-split-count">
              {s.count}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              {s.n_splits || 0} kinderen · ${(s.cost_usd || 0).toFixed(4)}
            </div>
          </div>
        </div>
      )}
      {!loading && data?.total_events === 0 && (
        <div className="text-center text-xs text-slate-500 py-2">
          Nog geen AI-verbruik in deze periode. De cijfers verschijnen zodra de eerste
          spraakopname of AI-schriftje wordt aangemaakt.
        </div>
      )}
      <div className="mt-3 text-[10px] text-slate-500 border-t border-purple-200/50 pt-2">
        <strong>Registratie:</strong> Alleen technische metadata (tokens, duur, kosten).
        Geen audio, geen transcript, geen medische inhoud opgeslagen.
      </div>
    </Card>
  );
}


function IncidentenShortcutCard() {  return (
    <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200/60" data-testid="incidenten-shortcut-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-700" />
            Incidenten
          </h3>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Registreer ongevallen, bijna-ongevallen en veiligheidsincidenten. Wordt automatisch meegenomen in het GGD-inspectiepakket.
          </p>
        </div>
        <a href="/app/incidenten" className="inline-flex">
          <Button variant="outline" data-testid="incidenten-open">
            Naar incidenten <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </a>
      </div>
    </Card>
  );
}


/** OrphanScanCard — toont de laatste dagelijkse tenant-integriteit-scan zodat
 * de directeur zonder navigeren ziet of er weesrecords zijn. Wordt gevoed
 * door de nachtelijke `consistency-audit` cron. */
function OrphanScanCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/cleanup/last-orphan-scan")
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return null;
  if (!data?.has_scan) {
    return (
      <Card className="p-4 border-slate-200 bg-slate-50" data-testid="orphan-scan-card-empty">
        <div className="text-sm text-slate-600 flex items-center gap-2">
          <Database className="h-4 w-4 text-slate-400" />
          Nog geen tenant-integriteit-scan uitgevoerd. De nachtelijke controle draait om 03:00 CET.
        </div>
      </Card>
    );
  }
  const total = data.total_orphans || 0;
  const clean = total === 0;
  const scanDate = data.created_at ? new Date(data.created_at) : null;
  const dateStr = scanDate
    ? scanDate.toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" })
    : "onbekend";
  return (
    <Card
      className={`p-5 border-2 ${clean ? "border-emerald-200 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}
      data-testid="orphan-scan-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-[260px]">
          <h3 className={`text-lg font-semibold flex items-center gap-2 ${clean ? "text-emerald-900" : "text-amber-900"}`}>
            {clean
              ? <CheckCircle2 className="h-5 w-5 text-emerald-700" />
              : <AlertTriangle className="h-5 w-5 text-amber-700" />}
            Tenant-integriteit — laatste scan
          </h3>
          <div className="text-xs text-slate-600 mt-1">Uitgevoerd: {dateStr}</div>
          {clean ? (
            <p className="text-sm text-emerald-900 mt-2">
              ✓ 0 weesrecords — alle groepen, kinderen, contracten en rooster-shifts
              verwijzen naar bestaande locaties/medewerkers.
            </p>
          ) : (
            <div className="mt-2">
              <div className="text-sm text-amber-900 mb-1">
                <strong>{total}</strong> weesrecord(s) gevonden. Categorieën:
              </div>
              <ul className="text-xs text-amber-900 list-disc ml-5 space-y-0.5">
                {Object.entries(data.per_category || {}).map(([k, n]) => (
                  <li key={k}><span className="font-mono">{k}</span>: {n}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {!clean && (
          <a href="/app/instellingen" className="inline-flex">
            <Button variant="outline" className="border-amber-500 text-amber-900 hover:bg-amber-100"
                    data-testid="orphan-scan-open">
              Opschonen <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </a>
        )}
      </div>
    </Card>
  );
}


function Fact({ label, v, warn = false }) {
  return (
    <div className={`rounded border p-2 ${warn ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50/50"}`}>
      <div className={`text-[11px] uppercase tracking-wide ${warn ? "text-amber-700" : "text-slate-500"}`}>{label}</div>
      <div className={`text-lg font-semibold ${warn ? "text-amber-900" : "text-slate-900"}`}>{v ?? "—"}</div>
    </div>
  );
}

function PipelineFunnelCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/directie/pipeline");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Pipeline niet beschikbaar");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  if (loading) return (
    <Card className="p-6" data-testid="pipeline-loading">
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Pipeline berekenen …
      </div>
    </Card>
  );
  if (error) return (
    <Card className="p-6 border-2 border-red-200 bg-red-50" data-testid="pipeline-error">
      <div className="text-red-800 text-sm">{error}</div>
    </Card>
  );
  const stages = data?.stages || [];
  const maxCount = Math.max(1, ...stages.map(s => s.count));
  return (
    <Card className="p-6" data-testid="pipeline-funnel-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <ArrowRight className="h-5 w-5 text-sky-700" />
            Aanmeldings-funnel
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Van eerste aanmelding tot actieve plaatsing. Conversie eind-tot-eind:{" "}
            <strong className="text-slate-900">{data?.conversie_end_to_end_pct ?? 0}%</strong>
            {" · "}
            <span className="text-slate-500">
              {data?.verloren?.afgewezen ?? 0} afgewezen · {data?.verloren?.geannuleerd ?? 0} geannuleerd
            </span>
          </p>
        </div>
      </div>
      <div className="space-y-3">
        {stages.map((s, i) => {
          const w = Math.max(6, (s.count / maxCount) * 100);
          return (
            <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
              <div className="flex items-center justify-between mb-1 text-sm">
                <span className="font-medium text-slate-900">{s.label}</span>
                <span className="tabular-nums text-slate-700">
                  <strong>{s.count}</strong>
                  <span className="text-slate-500"> · {s.aandeel_pct}%</span>
                  {i > 0 && s.uitval_van_vorige > 0 && (
                    <span className="text-red-600 text-xs ml-2" data-testid={`funnel-uitval-${s.key}`}>
                      ↓ {s.uitval_van_vorige} uitval
                    </span>
                  )}
                </span>
              </div>
              <div className="h-3 rounded bg-slate-100 overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${w}%`, backgroundColor: s.kleur }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function BelastingCard() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  // getMonth() is zero-based (jan=0), maar de backend + UI verwachten 1-12.
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/directie/belasting/aanlevering?year=${year}&month=${month}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Aanlevering kan niet worden opgevraagd");
      setData(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const download = async (force = false) => {
    setDownloading(true);
    try {
      const r = await api.get(
        `/directie/belasting/aanlevering.csv?year=${year}&month=${month}${force ? "&force=1" : ""}`,
        { responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `belastingdienst_aanlevering_${year}-${String(month).padStart(2, "0")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Aanlevering gedownload");
    } catch (e) {
      toast.error("Download mislukt");
    } finally { setDownloading(false); }
  };

  return (
    <Card className="p-6" data-testid="belasting-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Landmark className="h-5 w-5 text-amber-700" />
            Belastingdienst — Maandaanlevering Kinderopvangtoeslag
          </h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Preflight-validatie van BSN's, LRK-nummers, opvanguren en uurtarief per
            factuur. Fouten blokkeren de aanlevering; waarschuwingen kun je toch mee-exporteren.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Jaar</label>
          <Input type="number" min="2020" max="2100" value={year}
                 onChange={e => setYear(parseInt(e.target.value || "0", 10))}
                 data-testid="belasting-year" className="w-28" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Maand</label>
          <select value={month} onChange={e => setMonth(parseInt(e.target.value, 10))}
                  data-testid="belasting-month"
                  className="border border-slate-200 rounded px-3 py-2 text-sm w-32">
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(2000, i, 1).toLocaleString("nl-NL", { month: "long" })}
              </option>
            ))}
          </select>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}
                data-testid="belasting-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Valideer"}
        </Button>
      </div>
      {error && (
        <div className="rounded border-2 border-red-200 bg-red-50 p-3 text-sm text-red-800 mb-3"
             data-testid="belasting-error">{error}</div>
      )}
      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat label="Regels valide" value={data.counts?.valid ?? 0}
                  accent={data.counts?.valid ? "text-emerald-700" : "text-slate-500"}
                  testid="belasting-stat-valid"/>
            <Stat label="Warnings" value={data.counts?.warnings ?? 0}
                  accent="text-amber-700" testid="belasting-stat-warnings"/>
            <Stat label="Errors" value={data.counts?.errors ?? 0}
                  accent={data.counts?.errors ? "text-red-700" : "text-slate-500"}
                  testid="belasting-stat-errors"/>
            <Stat label="Totaal bedrag" value={eur(data.totaal_bedrag)}
                  sub={`${data.totaal_uren} uren`} testid="belasting-stat-totaal"/>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => download(false)}
                    disabled={!data.csv_ready || downloading}
                    className="bg-amber-700 hover:bg-amber-800"
                    data-testid="belasting-download-btn">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              Download CSV
            </Button>
            {data.counts?.warnings > 0 && data.counts?.errors === 0 && (
              <Button variant="outline" onClick={() => download(true)}
                      disabled={downloading}
                      data-testid="belasting-download-force-btn">
                Download inclusief warnings
              </Button>
            )}
          </div>
          {data.errors_rows?.length > 0 && (
            <div className="mt-4 rounded border border-red-200 bg-red-50/50 p-3 text-xs"
                 data-testid="belasting-errors-list">
              <div className="font-semibold text-red-800 mb-2 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> {data.errors_rows.length} fouten blokkeren aanlevering
              </div>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {data.errors_rows.slice(0, 10).map((r, i) => (
                  <li key={i}>
                    <span className="font-mono text-slate-700">{r.kind_naam}</span>
                    <span className="text-red-700"> — {(r.errors || []).join("; ")}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function ExtraOpvangdagenCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [reden, setReden] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/directie/extra-opvangdagen");
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kan verzoeken niet laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/directie/extra-opvangdagen/${id}/keur-goed`);
      toast.success(`Goedgekeurd — nog ${r.data.bkr.available - 1} plek(ken) vrij`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message || JSON.stringify(detail) : (detail || "Goedkeuring mislukt");
      toast.error(msg);
    } finally { setBusyId(null); }
  };

  const reject = async () => {
    if (!rejectFor || !reden.trim()) return;
    setBusyId(rejectFor);
    try {
      await api.post(`/directie/extra-opvangdagen/${rejectFor}/wijs-af`, { reden: reden.trim() });
      toast.success("Verzoek afgewezen");
      setRejectFor(null); setReden("");
      await load();
    } catch (e) { toast.error("Afwijzen mislukt"); }
    finally { setBusyId(null); }
  };

  return (
    <Card className="p-6" data-testid="extra-opvangdag-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Users className="h-5 w-5 text-teal-700" />
            Extra opvangdag — ouder-verzoeken
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Ouders vragen extra dagen aan; hier keur je goed met automatische BKR-check
            of wijs je af met reden.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="extra-opvangdag-refresh">
          Vernieuwen
        </Button>
      </div>
      {loading && (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Verzoeken laden …
        </div>
      )}
      {!loading && items.length === 0 && (
        <div className="text-sm text-slate-500 italic">Geen openstaande verzoeken.</div>
      )}
      {!loading && items.length > 0 && (
        <ul className="divide-y divide-slate-100" data-testid="extra-opvangdag-list">
          {items.map(r => (
            <li key={r.id} className="py-3 flex flex-wrap items-center gap-3"
                data-testid={`extra-opvangdag-item-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900">{r.kind_naam}</div>
                <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                  <span>{r.datum}</span>
                  <span>{r.dagdeel}</span>
                  {r.reden_ouder && <span>“{r.reden_ouder}”</span>}
                </div>
              </div>
              <Button size="sm" onClick={() => approve(r.id)} disabled={busyId === r.id}
                      className="bg-emerald-700 hover:bg-emerald-800"
                      data-testid={`extra-opvangdag-approve-${r.id}`}>
                {busyId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> :
                  <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Goedkeuren</>}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setRejectFor(r.id)}
                      data-testid={`extra-opvangdag-reject-${r.id}`}>
                <XCircle className="h-3.5 w-3.5 mr-1" /> Afwijzen
              </Button>
            </li>
          ))}
        </ul>
      )}
      {rejectFor && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4"
             data-testid="extra-opvangdag-reject-dialog"
             onClick={(e) => { if (e.target === e.currentTarget) setRejectFor(null); }}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-white rounded-lg shadow-2xl max-w-md w-full p-5">
            <h3 className="font-semibold text-slate-900 mb-2">Reden van afwijzing</h3>
            <textarea value={reden} onChange={e => setReden(e.target.value)}
                      rows={4} className="w-full border border-slate-200 rounded p-2 text-sm"
                      placeholder="Bijv. groep vol op deze dag"
                      data-testid="extra-opvangdag-reject-textarea" />
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => { setRejectFor(null); setReden(""); }}>
                Annuleren
              </Button>
              <Button onClick={reject} disabled={!reden.trim() || busyId}
                      className="bg-red-700 hover:bg-red-800"
                      data-testid="extra-opvangdag-reject-confirm">
                Afwijzen
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function DirectiePage() {
  const [tenantName, setTenantName] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/tenant/me");
        setTenantName(r.data?.name || "uw organisatie");
      } catch { setTenantName("uw organisatie"); }
    })();
  }, []);
  return (
    <div className="space-y-6" data-testid="directie-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Directie-cockpit</h1>
        <p className="text-slate-600 mt-1 text-sm">
          Kansen, compliance en pipeline voor {tenantName || "uw organisatie"} — in één blik.
        </p>
      </div>
      <PlatformAdminBar />
      <OrphanScanCard />
      <PipelineFunnelCard />
      <OmzetkansenCard />
      <ExtraOpvangdagenCard />
      <BelastingCard />
      <GgdExportCard />
      <GgdHistorieCard />
      <DrieUursRegelCard />
      <IncidentenShortcutCard />
      <AiUsageCard />
      <Card className="p-6 bg-gradient-to-br from-sky-50 to-cyan-50 border-sky-200/60"
            data-testid="pipeline-shortcut-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <MailCheck className="h-5 w-5 text-sky-700" />
              Aanmeldingspijplijn
            </h3>
            <p className="text-sm text-slate-600 mt-1 max-w-xl">
              Van publiek aanmeldformulier tot definitief contract. Alle aanbodmails hebben
              nu een preview-knop zodat je exact ziet wat de ouder ontvangt — geen echte
              verzending in demo-modus.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href="/app/aanmeldingen" className="inline-flex">
              <Button variant="outline" data-testid="pipeline-open-aanmeldingen">
                Open aanmeldingen <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </a>
            <a href="/app/wachtlijst" className="inline-flex">
              <Button variant="outline" data-testid="pipeline-open-wachtlijst">
                <Calendar className="h-4 w-4 mr-1" /> Wachtlijst
              </Button>
            </a>
          </div>
        </div>
      </Card>

      <MailDeliverabilityTile />
    </div>
  );
}


/**
 * WEBSTER KOV — Mail Deliverability Dashboard tegel.
 * Aggregeert `db.email_log` over de laatste 7 dagen: totaal + bounce-rate +
 * top-3 fout-redenen + laatste 10 bounces. Klik → in het log verder kijken.
 */
function MailDeliverabilityTile() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const [d, h] = await Promise.all([
        api.get("/mail/deliverability", { params: { days: 7 } })
          .then(r => r.data).catch(() => null),
        api.get("/mail/health").then(r => r.data).catch(() => null),
      ]);
      setData(d);
      setHealth(h);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const rate = data.bounce_rate_pct || 0;
  const healthy = health ? health.overall_healthy !== false : true;
  // Kies toon: als provider ongezond → altijd rood; anders bounce-rate
  const tone = !healthy ? "border-red-400 bg-red-50"
             : rate === 0 ? "border-emerald-200 bg-emerald-50/40"
             : rate < 5   ? "border-amber-200 bg-amber-50/40"
             :              "border-red-300 bg-red-50";
  const badgeTone = rate === 0 ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                : rate < 5   ? "bg-amber-100 text-amber-800 border-amber-200"
                :              "bg-red-100 text-red-800 border-red-200";

  // Human-readable label per health-probleem
  const problemLabels = {
    no_provider_configured: "Geen mailprovider ingesteld — mails vertrekken niet",
    dns_emergent_broken: "DNS naar mail-relay is onbereikbaar — DNS/netwerk-fout",
    sendgrid_sender_not_configured: "SendGrid sender is niet geverifieerd",
  };
  const problems = (health && health.problems) || [];

  return (
    <Card className={`p-5 border-2 ${tone}`} data-testid="mail-deliverability-tile">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <MailCheck className="h-5 w-5 text-slate-700" />
            Mail-bezorging (7 dagen)
          </h3>
          <p className="text-xs text-slate-600 mt-1">
            Auto-mails van aanbod, contract en meldingen — houd bounces onder 5%.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {health && (
            <Badge
              className={healthy
                ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                : "bg-red-100 text-red-800 border-red-200"}
              data-testid="mail-health-badge"
              title={healthy ? "Mailprovider is bereikbaar" : problems.join(", ")}
            >
              {healthy ? "provider ok" : "provider fout"}
            </Badge>
          )}
          <Badge className={badgeTone} data-testid="mail-bounce-rate">
            {rate}% bounce
          </Badge>
        </div>
      </div>

      {/* Provider-health banner (Prompt 07/frontend-widget) — verschijnt
          alleen als er een concrete DNS/provider-fout is. */}
      {!healthy && problems.length > 0 && (
        <div className="mb-3 rounded border border-red-300 bg-red-100/70 px-3 py-2 text-xs text-red-900"
             data-testid="mail-health-problems">
          <div className="font-semibold mb-1">⚠ Mail-relay problemen gedetecteerd</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {problems.map((p) => (
              <li key={p} data-testid={`mail-health-problem-${p}`}>
                {problemLabels[p] || p}
              </li>
            ))}
          </ul>
          <div className="mt-2 text-[10px] text-red-800/80">
            Deel dit met de systeembeheerder — mails vertrekken pas wanneer
            deze health-check groen wordt.
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 mb-4">
        <Stat label="Totaal" value={data.total} testid="mail-stat-total" />
        <Stat label="Bezorgd" value={data.sent} accent="text-emerald-700" testid="mail-stat-sent" />
        <Stat label="Gefaald" value={data.failed}
              accent={data.failed ? "text-red-700" : "text-slate-500"}
              testid="mail-stat-failed" />
      </div>
      {(data.top_reasons || []).length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Top redenen</div>
          <div className="space-y-1">
            {data.top_reasons.map((r, i) => (
              <div key={i} className="text-xs flex items-start justify-between gap-2 border-b border-slate-100 pb-1"
                   data-testid={`mail-top-reason-${i}`}>
                <span className="text-slate-700 flex-1">{r.reason}</span>
                <span className="font-mono text-slate-500">{r.count}×</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {(data.recent_failures || []).length > 0 && (
        <details className="text-xs" data-testid="mail-recent-failures">
          <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
            Laatste {data.recent_failures.length} gefaalde mails
          </summary>
          <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
            {data.recent_failures.map((f, i) => (
              <div key={i} className="border border-slate-200 rounded p-1.5 bg-white">
                <div className="font-mono text-[10px] text-slate-500">
                  {new Date(f.sent_at).toLocaleString("nl-NL")} · {f.type}
                </div>
                <div className="truncate"><strong>{f.to}</strong>{f.subject ? ` · ${f.subject}` : ""}</div>
                <div className="text-red-700 text-[11px]">{f.reason}</div>
              </div>
            ))}
          </div>
        </details>
      )}
      {loading && <div className="text-xs text-slate-400 mt-2">Laden…</div>}
    </Card>
  );
}
